﻿import {
  Loader2,
  Plus,
  Tag,
  Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { Promotion } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QueryError } from "@/components/query-error";
import { usePromotionsQuery } from "@/features/promotions/api";
import { formatMoneyTotals } from "@/features/promotions/format";
import { ISTANBUL_TZ, useFormat, type Format } from "@/lib/format";
import { useT } from "@/lib/i18n";

const PROMO_DATE_TIME: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};

export function PromotionsPage() {
  return (
    <DashboardPage>
      <PromotionsView />
    </DashboardPage>
  );
}

function PromotionsView() {
  const t = useT();
  const { data, isLoading, error, refetch, isRefetching } = usePromotionsQuery();
  const topPromotionId = data ? findTopPromotionId(data) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("promotions.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("promotions.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link to="/promotions/new">
            <Plus className="h-4 w-4" />
            {t("promotions.new")}
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <QueryError
          message={t("promotions.loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : !data || data.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <Tag className="h-8 w-8" />
            <div>
              <p className="font-medium text-foreground">
                {t("promotions.emptyTitle")}
              </p>
              <p className="mt-1 text-sm">{t("promotions.emptyDesc")}</p>
            </div>
            <Button asChild variant="outline">
              <Link to="/promotions/new">
                <Plus className="h-4 w-4" />
                {t("promotions.new")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {data.map((p) => (
            <PromotionRow key={p.id} promo={p} isTop={p.id === topPromotionId} />
          ))}
        </div>
      )}
    </div>
  );
}

function findTopPromotionId(promos: Promotion[]): string | null {
  let top: Promotion | null = null;
  for (const p of promos) {
    if (p.stats.paidOrderCount > (top?.stats.paidOrderCount ?? 0)) top = p;
  }
  return top?.id ?? null;
}

function PromotionRow({ promo, isTop }: { promo: Promotion; isTop: boolean }) {
  const t = useT();
  const fmt = useFormat();
  const now = new Date();
  const starts = promo.startsAt ? new Date(promo.startsAt) : null;
  const ends = promo.endsAt ? new Date(promo.endsAt) : null;

  const status = getStatus(promo, now, starts, ends);

  return (
    <Link to={`/promotions/${promo.id}`} className="block">
      <Card className="transition-colors hover:bg-accent/40">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="truncate text-base">{promo.name}</CardTitle>
              <Badge variant={status.variant}>{t(`promotions.status.${status.key}`)}</Badge>
              {promo.code ? (
                <Badge variant="outline" className="font-mono">
                  {promo.code}
                </Badge>
              ) : (
                <Badge variant="secondary">{t("promotions.automatic")}</Badge>
              )}
              {isTop && (
                <Badge variant="default" className="gap-1">
                  <Trophy className="h-3 w-3" />
                  {t("promotions.topBadge")}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {describePromotion(promo, t, fmt)}
            </p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>
                {t("promotions.usage")}: {fmt.number(promo.usageCount)}
                {promo.maxUses ? ` / ${fmt.number(promo.maxUses)}` : ""}
              </span>
              {promo.stats.totals.some((x) => x.discountTotal > 0) && (
                <span>
                  {t("promotions.statsDiscount")}:{" "}
                  {formatMoneyTotals(promo.stats.totals, "discountTotal", fmt)}
                </span>
              )}
              {promo.stats.freeTicketTotal > 0 && (
                <span>
                  {t("promotions.statsFreeTickets")}: {fmt.number(promo.stats.freeTicketTotal)}
                </span>
              )}
              {promo.stats.totals.length > 0 && (
                <span>
                  {t("promotions.statsRevenue")}:{" "}
                  {formatMoneyTotals(promo.stats.totals, "revenueTotal", fmt)}
                </span>
              )}
              {promo.eventTitle ? (
                <span>
                  {t("promotions.scope")}: {promo.eventTitle}
                </span>
              ) : (
                <span>{t("promotions.scopeAll")}</span>
              )}
              {starts ? (
                <span>
                  {t("promotions.starts")}: {fmt.dateTime(starts, PROMO_DATE_TIME)}
                </span>
              ) : null}
              {ends ? (
                <span>
                  {t("promotions.ends")}: {fmt.dateTime(ends, PROMO_DATE_TIME)}
                </span>
              ) : null}
            </div>
          </div>
        </CardHeader>
      </Card>
    </Link>
  );
}

type StatusVariant = "success" | "warning" | "secondary" | "outline";

function getStatus(
  promo: Promotion,
  now: Date,
  starts: Date | null,
  ends: Date | null,
): { key: "active" | "scheduled" | "expired" | "paused"; variant: StatusVariant } {
  if (!promo.isActive) return { key: "paused", variant: "secondary" };
  if (ends && now > ends) return { key: "expired", variant: "outline" };
  if (starts && now < starts) return { key: "scheduled", variant: "warning" };
  return { key: "active", variant: "success" };
}

function describePromotion(
  p: Promotion,
  t: ReturnType<typeof useT>,
  fmt: Format,
): string {
  switch (p.type) {
    case "BuyXGetY":
      return t("promotions.describeBogo", { buyX: p.buyX ?? 0, getY: p.getY ?? 0 });
    case "Percentage":
      return t("promotions.describePercentage", {
        value: fmt.percent(p.discountValue ?? 0, 2),
      });
    case "FixedAmount":
      return t("promotions.describeFixed", {
        value: p.discountCurrency
          ? fmt.money(p.discountValue ?? 0, p.discountCurrency)
          : fmt.number(p.discountValue ?? 0),
      });
  }
}
